import {
  useCreateDemoLinkMutation,
  useUpdateDemoLinkMutation,
} from '@carboncopyco/data';
import {
  type DemoLink,
  DemoLinkAccessPolicy,
  DemoLinkPlatform,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useObjectState } from '@uidotdev/usehooks';
import constate from 'constate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useCopy } from '../utils';
import type { DemoLinkModalContextProps, DemoLinkState } from './types';

function useDemoLinkContext({
  assetId,
  assetType,
  organizationId,
  isOpen,
  onClose,
  role,
  link,
  asset,
}: DemoLinkModalContextProps) {
  const flags = useFlags();

  const [
    createDemoLinkMutation,
    { data: createdDemoLink, isLoading: newIsLoading, isError: newIsError },
  ] = useCreateDemoLinkMutation();

  const [
    updateDemoLinkMutation,
    { isLoading: updatedIsLoading, isError: updatedIsError },
  ] = useUpdateDemoLinkMutation();

  const [linkState, setLinkState] = useObjectState<DemoLinkState>(
    link
      ? {
          enabled: link.enabled,
          allowSignUp: link.allowSignUp,
          platform: link.platform,
          withGiveADemo: link.withGiveADemo,
          revisionId: link.asset.autoSync
            ? 'autoSync'
            : String(link.asset.revisionId + 1),
          accessPolicy: link.accessPolicy,
          showGuidanceTooltip: link.showGuidanceTooltip,
        }
      : {
          enabled: true,
          allowSignUp: false,
          platform: DemoLinkPlatform.desktop,
          withGiveADemo: true,
          revisionId: 'autoSync',
          accessPolicy: DemoLinkAccessPolicy.WORKSPACE_RESTRICTED,
          showGuidanceTooltip: false,
        }
  );

  const convertToAssetRevision = useCallback(
    (revisionId: string) => {
      const autoSync = revisionId === 'autoSync';
      return {
        revisionId: autoSync ? asset.revisionId : Number(revisionId) - 1,
        autoSync,
      };
    },
    [asset.revisionId]
  );

  const updateLink = useCallback(
    (arg: DemoLinkState) => {
      updateDemoLinkMutation({
        organizationId,
        id: link?.id,
        enabled: arg.enabled,
        withGiveADemo: arg.withGiveADemo,
        asset: convertToAssetRevision(arg.revisionId),
        allowSignUp: arg.allowSignUp,
        accessPolicy: arg.accessPolicy,
        platform: arg.platform,
        showGuidanceTooltip: arg.showGuidanceTooltip,
      })
        .unwrap()
        .then(() => {
          notify.success('Link settings saved');
        });
    },
    [convertToAssetRevision, link?.id, organizationId, updateDemoLinkMutation]
  );

  const createLink = () => {
    createDemoLinkMutation({
      organizationId,
      assetId,
      assetType,
      enabled: linkState.enabled,
      withGiveADemo: linkState.withGiveADemo,
      asset: convertToAssetRevision(linkState?.revisionId),
      allowSignUp: linkState.allowSignUp,
      accessPolicy: linkState.accessPolicy,
      platform: linkState.platform,
      showGuidanceTooltip: linkState.showGuidanceTooltip,
    })
      .unwrap()
      .then(() => {
        notify.success('Link was generated successfully');
      });
  };

  const setAndUpdatelinkState = (arg: DemoLinkState) => {
    setLinkState(arg);
    link && updateLink(arg);
  };
  const setEnabled = (enabled: boolean) => {
    setAndUpdatelinkState({ ...linkState, enabled });
  };

  const setWithGiveADemo = (withGiveADemo: boolean) => {
    setAndUpdatelinkState({ ...linkState, withGiveADemo });
  };

  const setAllowSignUp = (allowSignUp: boolean) => {
    setAndUpdatelinkState({ ...linkState, allowSignUp });
  };

  const setAccessPolicy = (accessPolicy: DemoLinkAccessPolicy) => {
    setAndUpdatelinkState({ ...linkState, accessPolicy });
  };

  const setPlatform = (platform: DemoLinkPlatform) => {
    setAndUpdatelinkState({
      ...linkState,
      platform,
      showGuidanceTooltip: false,
    });
  };

  const setShowGuidanceTooltips = (showGuidanceTooltip: boolean) => {
    setAndUpdatelinkState({
      ...linkState,
      showGuidanceTooltip,
    });
  };

  const setRevision = (revisionId: string) => {
    setAndUpdatelinkState({
      ...linkState,
      revisionId,
    });
  };

  const { copyText: copyLink } = useCopy();

  const submit = createLink;

  const getLinkUrl = () => {
    return link?.url;
  };

  return {
    isOpen,
    onClose,
    assetId,
    assetType,
    submit,
    linkState,
    demoLink: link,
    allowSignup: linkState.allowSignUp,
    platform: linkState.platform,
    revisionId: linkState?.revisionId,
    createdDemoLink,
    isLoading: newIsLoading || updatedIsLoading,
    isError: newIsError || updatedIsError,
    allowWithoutGiveADemo: flags?.allowDemoLinkWithoutGiveADemo,
    setEnabled,
    setAllowSignUp,
    showGuidanceTooltip: linkState.showGuidanceTooltip,
    setShowGuidanceTooltips,
    setAccessPolicy,
    setPlatform,
    setRevision,
    setWithGiveADemo,
    getLinkUrl,
    copyLink,
    role,
    latestRevision: asset.revisionId,
    organizationId: organizationId,
  };
}

export const [
  DemoLinkModalProvider,
  useDemoLinkEnabled,
  useDemoLinkWithGiveADemo,
  useDemoLinkAllowSignUp,
  useDemoLinkAccessPolicy,
  useDemoLinkPlatform,
  useDemoLinkRevision,
  useDemoLinkSubmitAndCopy,
  useDemoLinkGuidanceTooltips,
  useDemoLinkProps,
] = constate(
  useDemoLinkContext,
  (value): [boolean, (val: boolean) => void] => [
    value.linkState.enabled,
    value.setEnabled,
  ],
  (value): [boolean, (val: boolean) => void, boolean] => [
    value.linkState.withGiveADemo,
    value.setWithGiveADemo,
    value.allowWithoutGiveADemo,
  ],
  (value): [boolean, (val: boolean) => void] => [
    value.linkState.allowSignUp,
    value.setAllowSignUp,
  ],
  (value): [DemoLinkAccessPolicy, (val: DemoLinkAccessPolicy) => void] => [
    value.linkState.accessPolicy,
    value.setAccessPolicy,
  ],
  (value): [DemoLinkPlatform, (val: DemoLinkPlatform) => void] => [
    value.linkState.platform,
    value.setPlatform,
  ],
  (value): [string, (revisionId: string) => void] => [
    value.linkState?.revisionId,
    value.setRevision,
  ],
  (value): [() => void, () => void, link: DemoLink] => [
    value.submit,
    () =>
      value.copyLink(value.getLinkUrl(), 'Link was copied to your clipboard'),
    value.demoLink,
  ],
  (value): [boolean, (val: boolean) => void] => [
    value.linkState.showGuidanceTooltip,
    value.setShowGuidanceTooltips,
  ],
  (value) => ({
    assetId: value.assetId,
    assetType: value.assetType,
    isOpen: value.isOpen,
    onClose: value.onClose,
    isLoading: value.isLoading,
    isError: value.isError,
    link: value.demoLink,
    role: value.role,
    enabled: value.linkState?.enabled,
    latestRevision: value.latestRevision,
    organizationId: value.organizationId,
  })
);
